import Reflux from "reflux";
import { fromJS, List, Map } from "immutable";
import { cloneDeep } from "lodash";

import ImageActions from "../actions/ImageActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import BlockUpdateVO from "../vo/BlockUpdate.mjs";
import UIActions from "@/actions/UIActions";
import UndoRedoManager from "@/managers/UndoRedoManager";
import {
  calculateOriginalImageSize,
  mapCropToDataModel,
  convertToZoomMode,
  getDimensions,
} from "@/utils/CropUtils";

import {
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  LAYOUTS,
  SYNCED_PROPS,
} from "@/constants/DesignConstants.mjs";
import { CROP_MODE, DEFAULT_MODE } from "@/constants/UIConstants";
import {
  GRAPHIC_STYLE,
  GRAPHIC_STYLE_OFFSET_X,
  GRAPHIC_STYLE_OFFSET_Y,
  GRAPHIC_STYLE_SCALE_PERCENTAGE,
  GRAPHIC_STYLE_TYPE,
} from "@/constants/MergeConstants";
import { getContainerData, positionElementToContainer } from "@/utils/GroupUtils";
import DesignViewStore from "./DesignViewStore";

/**
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS[number]} Layout
 * @typedef {import("@/utils/CropUtils").ImmutableDimensions} Dimensions
 */

const BASE_POSITIONS = {
  x: 0,
  y: 0,
  w: 0,
  h: 0,
  a: 0,
};

const BASE_LAYOUT_POSITIONS = {
  [LAYOUT_DESKTOP]: BASE_POSITIONS,
  [LAYOUT_MOBILE]: BASE_POSITIONS,
};

const ImageStore = Reflux.createStore({
  storeName: 'ImageStore',
  listenables: [ImageActions],

  init() {
    this.state = fromJS({
      // Original element data
      selectedImageId: null,
      selectedElementData: null,

      aspectRatio: 1,

      // original element dimensions. Used to draw frame.
      elementDimensions: cloneDeep(BASE_LAYOUT_POSITIONS),

      // Crop state. Updated at the end of each transform.
      currentCrop: cloneDeep(BASE_LAYOUT_POSITIONS),

      // Temporary dimensions holder during transform to hold the diff before committing on transform end.
      transformCrop: cloneDeep(BASE_LAYOUT_POSITIONS),

      initialCrop: cloneDeep(BASE_LAYOUT_POSITIONS),

      // containing element (groups/components)
      container: null,

      // previous mode to revert to on exit. Necessary for returning to group focus on exit, for example.
      prevMode: null,

      transformerElement: null,
      transforming: false,
    });
  },

  _resetData() {
    this.state = this.state
      .set("selectedImageId", null)
      .set("selectedImageData", null)
      .set("prevMode", null)
      .set("container", null);
    this.transforming = false;

    this.trigger(this.state);
  },

  onEnterCropMode(element, selectedBlock) {
    UndoRedoManager.save();

    const prevMode = DesignViewStore.getModeType();
    UIActions.setMode(CROP_MODE);

    const elementId = element.getIn(["selected", "id"]);
    const aspectRatio = element.getIn(["content", "aspect_ratio"]);
    // the selection path names a container only for genuinely contained elements
    const container = getContainerData(selectedBlock, elementId) ?? null;

    const { dimensions, newDimensions } = LAYOUTS.reduce(
      (result, layout) => {
        const dimensions = this._getDimensions(element, layout, container);

        const convertedDimensions = convertToZoomMode(dimensions, aspectRatio);

        // sizes to the image instead of element selection
        const originalImage = calculateOriginalImageSize(
          dimensions,
          dimensions.merge(convertedDimensions),
          aspectRatio
        );

        const newDimensions = dimensions.merge(originalImage).delete(GRAPHIC_STYLE);

        result.dimensions[layout] = dimensions;
        result.newDimensions[layout] = newDimensions;

        return result;
      },
      { dimensions: {}, newDimensions: {} }
    );

    // replace, not mergeDeep: the previous crop's keys (e.g. a group's pId) must not survive
    this.state = this.state.merge(
      fromJS({
        aspectRatio: Number(aspectRatio),
        selectedElementData: element,
        selectedImageId: elementId,
        elementDimensions: dimensions,
        initialCrop: newDimensions,
        currentCrop: newDimensions,
        transformerElement: element.mergeDeep(newDimensions),
        container,
        prevMode,
      }),
    );

    this.trigger(this.state);
  },

  onExitCropMode() {
    if (this.transforming) return;

    UIActions.setMode(this.state.get("prevMode"));
    this._resetData();
    // undo the added save point added on enter. Prevents redundant undos from stacking when entering and exiting multiple times without change
    DocumentStore.dispatch({ type: COMMANDS.UNDO });
    ImageActions.exitFinished();
  },

  onExitAndSaveCropMode() {
    if (this.transforming) return;

    const elementId = this.getElementId();
    const element = this.getSelectedElement();
    const aspectRatio = this.state.get("aspectRatio");
    const currentCrop = this.state.get("currentCrop");
    const selectedLayout = element.getIn(["selected", "layout"]);
    const container = this.state.get("container");
    const isMerged = element.get(SYNCED_PROPS, List()).includes(GRAPHIC_STYLE_TYPE);

    const affectedLayout = isMerged ? LAYOUT_DESKTOP : selectedLayout;

    const update = new BlockUpdateVO();

    const dimensions = this._getDimensions(element, selectedLayout, container);

    const layoutCrop = currentCrop.get(selectedLayout);

    // get ratio specific to active layout
    const { backgroundScale, horizontalOffset, verticalOffset } = mapCropToDataModel(
      dimensions,
      layoutCrop,
      aspectRatio
    );

    // apply the ratio to affectedLayout
    const widthCropUpdate = Map()
      .setIn(GRAPHIC_STYLE_TYPE.split("."), "zoom")
      .setIn(GRAPHIC_STYLE_SCALE_PERCENTAGE.split("."), backgroundScale)
      .setIn(GRAPHIC_STYLE_OFFSET_X.split("."), horizontalOffset)
      .setIn(GRAPHIC_STYLE_OFFSET_Y.split("."), verticalOffset);

    update.add(elementId, affectedLayout, widthCropUpdate);

    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });

    UIActions.setMode(this.state.get("prevMode"));
    this._resetData();
    ImageActions.exitFinished();
  },

  onStartTransform({ layout }) {
    this.transforming = true;
    this.transformLayout = layout;
  },

  onTransform(data) {
    const diffData = data[this.state.get("selectedImageId")];
    const { transformLayout } = this;

    const currentCrop = this._getCurrentCrop(transformLayout);

    // add diff to current prop creating the transform crop data
    const transformCrop = currentCrop
      .set("x", currentCrop.get("x") + diffData.xDiff)
      .set("y", currentCrop.get("y") + diffData.yDiff)
      .set("w", currentCrop.get("w") + diffData.wDiff)
      .set("h", currentCrop.get("h") + diffData.hDiff);

    this.state = this.state.setIn(["transformCrop", transformLayout], transformCrop);

    this.state = this.state.set(
      "transformerElement",
      this.getTransformerData().set(
        transformLayout,
        this.getTransformerData().get(transformLayout).merge(transformCrop)
      )
    );

    this.trigger();
    if (!this.transforming) {
      this.transforming = true;
    }
  },

  onEndTransform(data) {
    const { transformLayout } = this;

    // set current crop to the last transform crop
    const transformCrop = this.state.getIn(["transformCrop", transformLayout]);
    this.state = this.state.setIn(["currentCrop", transformLayout], transformCrop);
    this.transforming = false;
    this.transformLayout = null;
  },

  getTransformerData() {
    return this.state.get("transformerElement");
  },

  _getCurrentCrop(layout) {
    return this.state.getIn(["currentCrop", layout]);
  },

  /**
   * Gets the dimensions of the crop frame. Adds container offsets if provided.
   * @param {Map} element
   * @param {Layout} layout
   * @param {Map} container
   * @returns {Dimensions}
   */
  _getDimensions(element, layout, container) {
    let dimensions = getDimensions(element, layout);

    // apply container offsets to element
    if (container) {
      const containerDimensions = getDimensions(container, layout);
      return fromJS(
        positionElementToContainer(dimensions?.toJS(), containerDimensions?.toJS(), true)
      );
    }

    return dimensions;
  },

  getElementDimensions(layout) {
    return this.state.getIn(["elementDimensions", layout]);
  },

  getElementId() {
    return this.state.get("selectedImageId");
  },

  getSelectedElement() {
    return this.state.get("selectedElementData");
  },

  getAspectRatio() {
    return this.state.get("aspectRatio");
  },

  /**
   * Gets the container (e.g. group) the cropped image sits in, as resolved on crop entry.
   * @returns {Map | null} The container element, or null when the image is not contained.
   */
  getContainer() {
    return this.state.get("container");
  },

  getCurrentCrop(layout) {
    const { transforming, transformLayout } = this;
    const propName = transforming && layout === transformLayout ? "transformCrop" : "currentCrop";
    return this.state.getIn([propName, layout]);
  },

  getState() {
    return this.state;
  },

  getTransformingStatus() {
    return this.transforming;
  },
});

export default ImageStore;
